import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { getMyPermissions } from "../services/permissionService";
import { getUserId, getTeamID, JSONParser } from "../common/function";
import { authHeader } from "../common/mainfunction";
import { WORKFLOW_USER_ASSIGNMENTS_URL } from "../common/url";
import API from "../constants/API";
import type { Permission as ApiPermission } from "../types/api";

type Permission = ApiPermission;

interface QueueAssignment {
  queueName: string;
  [key: string]: unknown;
}

interface PermissionContextValue {
  // State
  permissions: Permission[];
  permissionKeys: string[];
  loading: boolean;
  error: string | null;

  // Queue assignments
  queueAssignments: QueueAssignment[];
  queueNames: string[];

  // Permission checks
  hasPermission: (permissionKey: string) => boolean;
  hasAnyPermission: (permissionKeyList: string[]) => boolean;
  hasAllPermissions: (permissionKeyList: string[]) => boolean;

  // Queue checks
  isAssignedToQueue: (queueName: string) => boolean;
  isAssignedToAnyQueue: (queueNameList: string[]) => boolean;

  // Actions
  refreshPermissions: () => Promise<void>;
  clearPermissions: () => void;
}

const PermissionContext = createContext<PermissionContextValue | undefined>(undefined);

interface PermissionProviderProps {
  children: React.ReactNode;
}

// ── Last-known-good persistence (per tenant+user) — UAT-1 ────────────────────
// Same flaw as FeatureFlagContext: the provider remounts on every full page
// reload, started from empty permissions, and the old retry window (2×1s) was
// shorter than an API container replacement (~10-20s LB outage). A reload during
// an outage collapsed every permission-gated screen for the rest of the session.
// Permissions must be sticky: hydrate last-known-good from sessionStorage and
// retry with backoff; a failed (re)fetch NEVER downgrades known-good state.
const PERMS_CACHE_PREFIX = "ezc.permissions.v1.";
const QUEUES_CACHE_PREFIX = "ezc.queueAssignments.v1.";

interface CachedPermissions {
  permissions: Permission[];
  permissionKeys: string[];
}

const cacheSuffix = (): string | null => {
  const userId = getUserId();
  const teamId = getTeamID();
  return userId && teamId ? `${teamId}.${userId}` : null;
};

const readSessionJSON = <T,>(key: string | null): T | null => {
  if (!key) return null;
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? (parsed as T) : null;
  } catch {
    return null;
  }
};

const writeSessionJSON = (key: string | null, value: unknown): void => {
  if (!key) return;
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage full/unavailable — non-fatal, state still lives in React.
  }
};

// Backoff schedule: 2s, 4s, 8s, 16s, 30s, 30s (~90s coverage) — long enough to
// ride out an API container replacement.
const PERMS_FETCH_MAX_RETRIES = 6;
const PERMS_FETCH_RETRY_BASE_MS = 2000;
const PERMS_FETCH_RETRY_MAX_MS = 30000;
const fetchBackoffMs = (attempt: number): number =>
  Math.min(PERMS_FETCH_RETRY_MAX_MS, PERMS_FETCH_RETRY_BASE_MS * 2 ** attempt);

/**
 * Permission Provider
 * Provides permission state and helper functions to the entire app
 */
export const PermissionProvider = ({ children }: PermissionProviderProps) => {
  const suffix = cacheSuffix();
  const [permissions, setPermissions] = useState<Permission[]>(() => {
    const cached = readSessionJSON<CachedPermissions>(suffix && PERMS_CACHE_PREFIX + suffix);
    return cached?.permissions ?? [];
  });
  const [permissionKeys, setPermissionKeys] = useState<string[]>(() => {
    const cached = readSessionJSON<CachedPermissions>(suffix && PERMS_CACHE_PREFIX + suffix);
    return cached?.permissionKeys ?? [];
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Queue assignment state (hydrated from last-known-good, refreshed below)
  const [queueAssignments, setQueueAssignments] = useState<QueueAssignment[]>(() => {
    return readSessionJSON<QueueAssignment[]>(suffix && QUEUES_CACHE_PREFIX + suffix) ?? [];
  });
  const [queueNames, setQueueNames] = useState<string[]>(() => {
    const cached = readSessionJSON<QueueAssignment[]>(suffix && QUEUES_CACHE_PREFIX + suffix);
    return Array.isArray(cached) ? cached.map((q) => q.queueName) : [];
  });

  const permsRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const queuesRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef<boolean>(true);

  /**
   * Load user's permissions from API with retry/backoff.
   * Failures keep the current (last-known-good) permissions — only a successful
   * response ever updates state.
   */
  const loadUserPermissions = useCallback(async (retryCount: number = 0): Promise<void> => {
    const scheduleRetry = (reason: string): void => {
      if (!mountedRef.current) return;
      if (retryCount + 1 >= PERMS_FETCH_MAX_RETRIES) {
        console.warn(
          `[PermissionContext] ${reason} — giving up after ${retryCount + 1} attempts; keeping last-known-good permissions`
        );
        setError(reason);
        return;
      }
      const delay = fetchBackoffMs(retryCount);
      console.warn(
        `[PermissionContext] ${reason} — keeping last-known-good permissions, retrying in ${delay}ms (attempt ${retryCount + 1}/${PERMS_FETCH_MAX_RETRIES})`
      );
      permsRetryTimerRef.current = setTimeout(() => {
        permsRetryTimerRef.current = null;
        void loadUserPermissions(retryCount + 1);
      }, delay);
    };

    try {
      if (retryCount === 0) {
        setLoading(true);
        setError(null);
      }
      const userId = getUserId();

      if (!userId) {
        // User not logged in, clear permissions
        setPermissions([]);
        setPermissionKeys([]);
        return;
      }

      // [G-SELFPERMS-01] Self-scoped fetch (GET /auth/me) — works for every authenticated
      // user. The admin-guarded /admin/users/:id/permissions 403'd for all non-admins and
      // collapsed every permission-gated screen. Admin views of OTHER users keep using the
      // admin endpoint (permissionService.getUserPermissions in AddUpdateteam).
      const response = await getMyPermissions();

      if (response.status === "Success" && response.data) {
        const perms = response.data.permissions || [];
        const keys = response.data.permissionKeys || [];
        if (mountedRef.current) {
          setPermissions(perms);
          setPermissionKeys(keys);
          setError(null);
        }
        // Persist last-known-good so a remount during an API outage hydrates
        // these instead of collapsing to no permissions.
        const key = cacheSuffix();
        writeSessionJSON(key && PERMS_CACHE_PREFIX + key, {
          permissions: perms,
          permissionKeys: keys,
        });
      } else {
        scheduleRetry("API returned non-success");
      }
    } catch (err: unknown) {
      scheduleRetry(`fetch failed (${err instanceof Error ? err.message : String(err)})`);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  /**
   * Load queue assignments from workflow API with the same retry/backoff +
   * last-known-good semantics as permissions.
   */
  const loadQueueAssignments = useCallback(async (retryCount: number = 0): Promise<void> => {
    const scheduleRetry = (reason: string): void => {
      if (!mountedRef.current) return;
      if (retryCount + 1 >= PERMS_FETCH_MAX_RETRIES) {
        console.warn(
          `[PermissionContext] queue assignments: ${reason} — giving up after ${retryCount + 1} attempts; keeping last-known-good`
        );
        return;
      }
      const delay = fetchBackoffMs(retryCount);
      console.warn(
        `[PermissionContext] queue assignments: ${reason} — keeping last-known-good, retrying in ${delay}ms (attempt ${retryCount + 1}/${PERMS_FETCH_MAX_RETRIES})`
      );
      queuesRetryTimerRef.current = setTimeout(() => {
        queuesRetryTimerRef.current = null;
        void loadQueueAssignments(retryCount + 1);
      }, delay);
    };

    try {
      const userId = getUserId();
      const teamId = getTeamID();
      if (!userId || !teamId) return;

      const config = {
        method: "GET",
        url: WORKFLOW_USER_ASSIGNMENTS_URL,
        headers: authHeader(),
        params: { userId: parseInt(userId, 10), teamId: parseInt(teamId, 10) },
      };

      const response = await API(config);
      const decrypted = response.data;
      const parsed = JSONParser(decrypted, { status: null, data: [] });

      if (parsed.status === "Success" && parsed.data) {
        if (mountedRef.current) {
          setQueueAssignments(parsed.data);
          setQueueNames(parsed.data.map((q: QueueAssignment) => q.queueName));
        }
        const key = cacheSuffix();
        writeSessionJSON(key && QUEUES_CACHE_PREFIX + key, parsed.data);
      } else {
        scheduleRetry("API returned non-success");
      }
    } catch (err) {
      scheduleRetry(`fetch failed (${err instanceof Error ? err.message : String(err)})`);
    }
  }, []);

  /**
   * Load permissions on mount and when userId changes
   */
  useEffect(() => {
    mountedRef.current = true;
    const userId = getUserId();
    if (userId) {
      loadUserPermissions();
      loadQueueAssignments();
    } else {
      setLoading(false);
    }
    return () => {
      mountedRef.current = false;
      if (permsRetryTimerRef.current) {
        clearTimeout(permsRetryTimerRef.current);
        permsRetryTimerRef.current = null;
      }
      if (queuesRetryTimerRef.current) {
        clearTimeout(queuesRetryTimerRef.current);
        queuesRetryTimerRef.current = null;
      }
    };
  }, [loadUserPermissions, loadQueueAssignments]);

  /**
   * Check if user has a specific permission
   */
  const hasPermission = useCallback(
    (permissionKey: string): boolean => {
      return permissionKeys.includes(permissionKey);
    },
    [permissionKeys]
  );

  /**
   * Check if user has ANY of the specified permissions
   */
  const hasAnyPermission = useCallback(
    (permissionKeyList: string[]): boolean => {
      if (!Array.isArray(permissionKeyList)) return false;
      return permissionKeyList.some((key) => permissionKeys.includes(key));
    },
    [permissionKeys]
  );

  /**
   * Check if user has ALL of the specified permissions
   */
  const hasAllPermissions = useCallback(
    (permissionKeyList: string[]): boolean => {
      if (!Array.isArray(permissionKeyList)) return false;
      return permissionKeyList.every((key) => permissionKeys.includes(key));
    },
    [permissionKeys]
  );

  /**
   * Check if user is assigned to a specific queue
   */
  const isAssignedToQueue = useCallback(
    (queueName: string): boolean => queueNames.includes(queueName),
    [queueNames]
  );

  /**
   * Check if user is assigned to any of the specified queues
   */
  const isAssignedToAnyQueue = useCallback(
    (queueNameList: string[]): boolean => {
      if (!Array.isArray(queueNameList)) return false;
      return queueNameList.some((name) => queueNames.includes(name));
    },
    [queueNames]
  );

  /**
   * Refresh permissions from API
   */
  const refreshPermissions = useCallback(async (): Promise<void> => {
    // A manual refresh supersedes any pending retries.
    if (permsRetryTimerRef.current) {
      clearTimeout(permsRetryTimerRef.current);
      permsRetryTimerRef.current = null;
    }
    if (queuesRetryTimerRef.current) {
      clearTimeout(queuesRetryTimerRef.current);
      queuesRetryTimerRef.current = null;
    }
    loadQueueAssignments();
    return loadUserPermissions();
  }, [loadUserPermissions, loadQueueAssignments]);

  /**
   * Clear permissions (e.g., on logout)
   */
  const clearPermissions = useCallback((): void => {
    setPermissions([]);
    setPermissionKeys([]);
    setQueueAssignments([]);
    setQueueNames([]);
    const key = cacheSuffix();
    if (key) {
      try {
        sessionStorage.removeItem(PERMS_CACHE_PREFIX + key);
        sessionStorage.removeItem(QUEUES_CACHE_PREFIX + key);
      } catch {
        // ignore storage errors
      }
    }
  }, []);

  const value: PermissionContextValue = {
    // State
    permissions,
    permissionKeys,
    loading,
    error,

    // Queue assignments
    queueAssignments,
    queueNames,

    // Permission checks
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,

    // Queue checks
    isAssignedToQueue,
    isAssignedToAnyQueue,

    // Actions
    refreshPermissions,
    clearPermissions,
  };

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
};

/**
 * Hook to access permission context
 */
export const usePermissions = (): PermissionContextValue => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error("usePermissions must be used within a PermissionProvider");
  }
  return context;
};

/**
 * Permission constants for easy reference (21 permissions)
 */
export const PERMISSIONS = {
  // Invoice (6)
  INVOICE_VIEW_ASSIGNED: "invoice.view.assigned",
  INVOICE_VIEW_ALL: "invoice.view.all",
  INVOICE_UPLOAD: "invoice.upload",
  INVOICE_EDIT: "invoice.edit",
  INVOICE_APPROVE: "invoice.approve",
  INVOICE_LOCK: "invoice.lock",
  INVOICE_DELETE: "invoice.delete",

  // Exception (5)
  EXCEPTION_REQUEST_INFO: "exception.handle.request_info",
  EXCEPTION_SUPPLIER: "exception.handle.supplier",
  EXCEPTION_SPECIALIST: "exception.handle.specialist",
  EXCEPTION_REJECTION: "exception.handle.rejection",
  EXCEPTION_DUPLICATE: "exception.handle.duplicate",

  // Admin (10)
  ADMIN_USERS_VIEW: "admin.users.view",
  ADMIN_USERS_MANAGE: "admin.users.manage",
  ADMIN_USERS_PERMISSIONS: "admin.users.permissions",
  ADMIN_SETTINGS_VIEW: "admin.settings.view",
  ADMIN_SETTINGS_MANAGE: "admin.settings.manage",
  ADMIN_SUPPLIERS_VIEW: "admin.suppliers.view",
  ADMIN_SUPPLIERS_MANAGE: "admin.suppliers.manage",
  ADMIN_REPORTS_VIEW: "admin.reports.view",
  ADMIN_GLCODES_MANAGE: "admin.glcodes.manage",
  ADMIN_AUDIT_VIEW: "admin.audit.view",
} as const;

export type PermissionKey = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

export default PermissionContext;
